import { useState, useEffect, useCallback } from "react";
import { supabase, getAuthHeader } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import * as Sentry from "@sentry/react";

/**
 * Studio session state: the project, its turn history, and generation.
 *
 * A session is a persistent, contextual project. Composer settings live on the
 * session so they carry across turns, and prior prompts are replayed to the
 * model as continuity (capped by the tenant's max_context_turns rather than
 * resending the whole history).
 */

export interface StudioAttachment {
  kind: "upload" | "product" | "prior_output";
  url: string;
  name: string;
  mime?: string;
}

export interface StudioTurn {
  id: string;
  sequence: number;
  prompt: string | null;
  attachments: StudioAttachment[];
  visualIds: string[];
  /** Resolved public URLs for the generated visuals. */
  outputs: { id: string; url: string }[];
  model: string | null;
  aspectRatio: string | null;
  resolution: string | null;
  error: string | null;
  createdAt: string;
  /** True while this turn is still generating (optimistic row). */
  pending?: boolean;
}

export interface StudioSettings {
  model?: string;
  aspectRatio: string;
  resolution: string;
  count: number;
  cameraConfigId?: string | null;
  productId?: string | null;
  /** Optional brief that supplies the structured base prompt. */
  briefConfigId?: string | null;
  briefAnswers?: Record<string, string>;
}

export interface StudioConfig {
  enabled: boolean;
  allow_uploads: boolean;
  allow_model_picker: boolean;
  allow_camera_picker: boolean;
  allow_resolution_picker: boolean;
  allow_product_attach: boolean;
  allowed_models: string[];
  max_resolution: string;
  max_images_per_turn: number;
  max_context_turns: number;
}

const DEFAULT_CONFIG: StudioConfig = {
  enabled: true,
  allow_uploads: true,
  allow_model_picker: true,
  allow_camera_picker: true,
  allow_resolution_picker: true,
  allow_product_attach: true,
  allowed_models: [],
  max_resolution: "4K",
  max_images_per_turn: 4,
  max_context_turns: 6,
};

/** The name a session carries until something better is known. */
export const UNTITLED_SESSION = "Untitled session";

/**
 * A session name taken from the first thing asked for.
 *
 * Sessions were all called "Untitled session", which made the list unusable
 * once there were more than about three — every row looked the same and only
 * the thumbnail distinguished them. A scene-started session gets its name up
 * front from the scene and product; a free-prompt one gets it from the first
 * prompt, which is not known until the first generation.
 *
 * Cut on a word boundary rather than mid-word, and only when that leaves
 * something readable — a hard slice produces "Warm living room with a Dai".
 */
export function sessionNameFromPrompt(prompt: string, max = 48): string {
  const clean = prompt.replace(/\s+/g, " ").trim();
  if (!clean) return UNTITLED_SESSION;
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
}

export const DEFAULT_SETTINGS: StudioSettings = {
  // 16:9 because the work is product photography for web and campaigns, where
  // a wide frame is what gets used. A square was a neutral-looking default
  // that quietly made every first render the wrong shape.
  aspectRatio: "16:9",
  resolution: "1K",
  count: 1,
};

function publicUrlFor(path: string): string {
  const { data } = supabase.storage.from("renders").getPublicUrl(path);
  return data.publicUrl;
}

export function useStudioSession(sessionId: string | null) {
  const { workspace } = useWorkspace();
  const { user } = useAuth();

  const [turns, setTurns] = useState<StudioTurn[]>([]);
  const [settings, setSettings] = useState<StudioSettings>(DEFAULT_SETTINGS);
  const [sessionName, setSessionName] = useState(UNTITLED_SESSION);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Tenant configuration drives which composer controls exist at all.
  const config: StudioConfig = {
    ...DEFAULT_CONFIG,
    ...(((workspace as any)?.studio_config as Partial<StudioConfig>) || {}),
  };

  // ── Load session + history ──────────────────────────────────────────────
  useEffect(() => {
    // Reset first so switching sessions never shows the previous one's turns.
    setTurns([]);
    setSettings(DEFAULT_SETTINGS);
    setSessionName(UNTITLED_SESSION);
    setError(null);

    if (!sessionId) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      const { data: session } = await supabase
        .from("studio_sessions" as any)
        .select("name, settings")
        .eq("id", sessionId)
        .maybeSingle();
      if (cancelled) return;
      if (session) {
        setSessionName((session as any).name || UNTITLED_SESSION);
        setSettings({ ...DEFAULT_SETTINGS, ...(((session as any).settings as StudioSettings) || {}) });
      }

      const { data: turnRows } = await supabase
        .from("studio_turns" as any)
        .select("*")
        .eq("session_id", sessionId)
        .order("sequence", { ascending: true });
      if (cancelled) return;

      const rows = (turnRows as any[]) || [];
      const allVisualIds = rows.flatMap((t) => t.visual_ids || []);
      const urlById = new Map<string, string>();
      if (allVisualIds.length > 0) {
        const { data: visuals } = await supabase
          .from("visuals")
          .select("id, storage_path")
          .in("id", allVisualIds);
        for (const v of (visuals as any[]) || []) {
          if (v.storage_path) urlById.set(v.id, publicUrlFor(v.storage_path));
        }
      }
      if (cancelled) return;

      setTurns(rows.map((t) => ({
        id: t.id,
        sequence: t.sequence,
        prompt: t.prompt,
        attachments: (t.attachments as StudioAttachment[]) || [],
        visualIds: t.visual_ids || [],
        outputs: (t.visual_ids || [])
          .filter((id: string) => urlById.has(id))
          .map((id: string) => ({ id, url: urlById.get(id)! })),
        model: t.model,
        aspectRatio: t.aspect_ratio,
        resolution: t.resolution,
        error: t.error,
        createdAt: t.created_at,
      })));
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [sessionId]);

  /** Persist composer settings so the session keeps its setup. */
  const updateSettings = useCallback(async (patch: Partial<StudioSettings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    if (sessionId) {
      await supabase
        .from("studio_sessions" as any)
        .update({ settings: next as any, updated_at: new Date().toISOString() } as any)
        .eq("id", sessionId);
    }
  }, [settings, sessionId]);

  const renameSession = useCallback(async (name: string) => {
    setSessionName(name);
    if (sessionId) {
      await supabase.from("studio_sessions" as any).update({ name } as any).eq("id", sessionId);
    }
  }, [sessionId]);

  /** Upload a file to the studio bucket and return an attachment. */
  const uploadAttachment = useCallback(async (file: File): Promise<StudioAttachment | null> => {
    if (!user) return null;
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("studio-uploads")
        .upload(path, file, { contentType: file.type });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("studio-uploads").getPublicUrl(path);
      return { kind: "upload", url: data.publicUrl, name: file.name, mime: file.type };
    } catch (err) {
      console.error("Studio upload failed:", err);
      Sentry.captureException(err);
      return null;
    }
  }, [user]);

  // ── Generate ────────────────────────────────────────────────────────────
  const generate = useCallback(async (prompt: string, attachments: StudioAttachment[]) => {
    if (!sessionId || !workspace || !user || isGenerating) return;
    // With a brief selected the brief itself is the prompt.
    if (!prompt.trim() && !settings.briefConfigId) return;

    setIsGenerating(true);
    setError(null);

    const sequence = turns.length > 0 ? Math.max(...turns.map((t) => t.sequence)) + 1 : 1;
    const optimisticId = crypto.randomUUID();

    // Show the turn immediately; fill in outputs when generation returns.
    setTurns((prev) => [...prev, {
      id: optimisticId,
      sequence,
      prompt,
      attachments,
      visualIds: [],
      outputs: [],
      model: settings.model || null,
      aspectRatio: settings.aspectRatio,
      resolution: settings.resolution,
      error: null,
      createdAt: new Date().toISOString(),
      pending: true,
    }]);

    let visualIds: string[] = [];
    let usedModel: string | null = null;
    let failure: string | null = null;

    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/studio-generate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: authHeader },
          body: JSON.stringify({
            session_id: sessionId,
            workspace_id: workspace.id,
            prompt,
            attachmentUrls: attachments.filter((a) => a.kind === "upload").map((a) => a.url),
            productId: settings.productId || undefined,
            cameraConfigId: settings.cameraConfigId || undefined,
            briefConfigId: settings.briefConfigId || undefined,
            briefAnswers: settings.briefAnswers || {},
            model: settings.model,
            aspectRatio: settings.aspectRatio,
            resolution: settings.resolution,
            count: settings.count,
            // Continuity without resending every image.
            contextTurns: turns.map((t) => t.prompt).filter(Boolean).slice(-config.max_context_turns),
          }),
        },
      );

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Generation failed (${response.status})`);
      }

      const data = await response.json();
      visualIds = data.visualIds || [];
      usedModel = data.model || null;
      if (visualIds.length === 0) throw new Error("No image was returned");
    } catch (err: any) {
      console.error("Studio generation error:", err);
      Sentry.captureException(err);
      failure = err?.message || "Generation failed";
      setError(failure);
    }

    // Resolve output URLs.
    const outputs: { id: string; url: string }[] = [];
    if (visualIds.length > 0) {
      const { data: visuals } = await supabase
        .from("visuals")
        .select("id, storage_path")
        .in("id", visualIds);
      for (const v of (visuals as any[]) || []) {
        if (v.storage_path) outputs.push({ id: v.id, url: publicUrlFor(v.storage_path) });
      }
    }

    // Persist the turn — history survives reload even when generation failed,
    // so a failed attempt is visible rather than silently vanishing.
    const { data: saved } = await supabase
      .from("studio_turns" as any)
      .insert({
        session_id: sessionId,
        sequence,
        prompt,
        attachments: attachments as any,
        visual_ids: visualIds,
        model: usedModel,
        aspect_ratio: settings.aspectRatio,
        resolution: settings.resolution,
        error: failure,
      } as any)
      .select("id")
      .maybeSingle();

    // Name the session after the first thing asked of it. Only while it is
    // still untitled — a name the user typed, or one a scene supplied, is
    // never overwritten. Skipped when the prompt is empty, which happens when
    // a scene carries the whole request.
    if (sessionName === UNTITLED_SESSION && prompt.trim()) {
      const derived = sessionNameFromPrompt(prompt);
      setSessionName(derived);
      await supabase
        .from("studio_sessions" as any)
        .update({ name: derived } as any)
        .eq("id", sessionId);
    }

    setTurns((prev) => prev.map((t) => t.id === optimisticId
      ? {
          ...t,
          id: (saved as any)?.id || t.id,
          visualIds,
          outputs,
          model: usedModel,
          error: failure,
          pending: false,
        }
      : t));

    await supabase
      .from("studio_sessions" as any)
      .update({ updated_at: new Date().toISOString() } as any)
      .eq("id", sessionId);

    setIsGenerating(false);
  }, [sessionId, workspace, user, isGenerating, turns, settings, config.max_context_turns, sessionName]);

  return {
    turns, settings, sessionName, config,
    isGenerating, error, loading,
    updateSettings, renameSession, uploadAttachment, generate,
    setError,
  };
}

/** Create a session and return its id. */
export async function createStudioSession(
  workspaceId: string,
  userId: string,
  initialSettings?: Partial<StudioSettings>,
  name = UNTITLED_SESSION,
): Promise<string | null> {
  // Settings go in at insert time rather than being patched afterwards. The
  // caller navigates to ?session=<id> straight after, which remounts the hook
  // and reloads settings from the row — a later patch would race that reload
  // and lose, which is how a product preselected from the product editor used
  // to vanish the moment the session opened.
  const { data, error } = await supabase
    .from("studio_sessions" as any)
    .insert({
      workspace_id: workspaceId,
      user_id: userId,
      name,
      settings: { ...DEFAULT_SETTINGS, ...(initialSettings || {}) } as any,
    } as any)
    .select("id")
    .maybeSingle();
  if (error) {
    console.error("Failed to create studio session:", error);
    Sentry.captureException(error);
    return null;
  }
  return (data as any)?.id || null;
}
